import React, { useState } from "react";
import { ArrowRight, Code, Palette, Rocket, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LabsMultiStepForm } from "@/components/LabsForm/LabsMultiStepForm";
import Footer from "@/components/Footer";

const Labs: React.FC = () => {
  const [success, setSuccess] = useState(false);

  if (success) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex flex-col">
        <div className="flex-grow flex items-center justify-center p-6">
          <div className="max-w-xl w-full bg-[#080815]/80 backdrop-blur-sm border border-white/10 rounded-xl p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-purple-500/5 to-transparent opacity-70"></div>
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Rocket className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-white">Thank you for your request!</h2>
              <p className="mb-6 text-white/80">We've received your information and will be in touch soon with a tailored plan for your project.</p>
              <Button 
                onClick={() => setSuccess(false)}
                className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 text-white"
              >
                Submit another request
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-black py-16">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0c0c1d] to-[#0f0923] opacity-90">
            {/* Generate some stars similar to footer */}
            {Array.from({ length: 50 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white animate-pulse"
                style={{
                  width: `${Math.random() * 2 + 1}px`,
                  height: `${Math.random() * 2 + 1}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  opacity: Math.random() * 0.7 + 0.3,
                  animationDelay: `${Math.random() * 5}s`,
                }}
              />
            ))}
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary via-cyan-400 to-purple-500">
                beaUX labs
              </h1>
              <p className="text-xl text-white/80 mb-8">
                Transform your product ideas into beautiful digital experiences.
                Go from concept to MVP, efficiently and effectively.
              </p>
              <Button
                className="bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 text-white px-8 py-6 text-lg"
                onClick={() => {
                  const formElement = document.getElementById('contact-form');
                  formElement?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Get Started <ArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-[#05050f]">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">The Process</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <ProcessStep 
                number="1" 
                title="Refine Your Concept" 
                description="Utilize our AI assistant to map out your MVP with features and requirements."
              />
              <ProcessStep 
                number="2" 
                title="Review" 
                description="We'll perform a discovery iteration process with you to ensure scope and deliverables are clearly defined."
              />
              <ProcessStep 
                number="3" 
                title="Development" 
                description="Our team will create a custom development roadmap for constant communication, so you can see the milestones for your specific project."
              />
              <ProcessStep 
                number="4" 
                title="Quality Assurance" 
                description="Comprehensive end-to-end testing to ensure your app functions to its full potential and is highly secure."
              />
            </div>
          </div>
        </section>
        
        {/* Form Section */}
        <section id="contact-form" className="py-16 bg-[#030308]">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-bold mb-2 text-white">Start Here</h2>
                <p className="text-white/80 max-w-2xl mx-auto">
                  Use our optimized form to refine your MVP. Let's bring your project to life.
                </p>
              </div>
              
              {/* New Multi-Step Form */}
              <LabsMultiStepForm onSuccess={() => setSuccess(true)} />
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

// Helper component for service cards
const ServiceCard: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
}> = ({ title, description, icon }) => (
  <div className="bg-[#0a0a1a] rounded-xl p-8 shadow-md border border-white/10 hover:border-primary/30 transition-all group relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
    <div className="relative z-10">
      <div className="text-4xl mb-4 bg-[#12121f] p-3 rounded-lg inline-block">{icon}</div>
      <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
      <p className="text-white/70">{description}</p>
    </div>
  </div>
);

// Helper component for process steps
const ProcessStep: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <div className="text-center p-6 relative">
    <div className="mx-auto bg-gradient-to-br from-primary to-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center mb-4 shadow-lg shadow-primary/20 font-bold text-lg">
      {number}
    </div>
    <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
    <p className="text-white/70">{description}</p>
    
    {/* Only show connector lines between steps (not after the last one) */}
    {number !== "4" && (
      <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary/50 to-transparent"></div>
    )}
  </div>
);

export default Labs;